import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-center" }
const _hoisted_2 = { class: "flex grow justify-center w-full font-sourcesanspro-semibold text-l2" }
const _hoisted_3 = {
  key: 0,
  class: "flex justify-center items-center h-full"
}
const _hoisted_4 = {
  key: 1,
  class: "flex flex-col justify-center items-center h-full p-4"
}
const _hoisted_5 = { class: "bg-white border rounded-lg p-8 flex flex-col items-center" }
const _hoisted_6 = { class: "text-xl font-semibold text-gray-800 mb-2" }
const _hoisted_7 = { class: "text-gray-600 text-center" }
const _hoisted_8 = {
  key: 2,
  class: "p-[34px]"
}
const _hoisted_9 = {
  key: 3,
  class: "flex flex-col justify-center items-center h-full p-4"
}
const _hoisted_10 = { class: "bg-white rounded-lg p-8 flex flex-col items-center w-full max-w-md" }
const _hoisted_11 = { class: "text-xl font-semibold text-gray-800 mb-2" }
const _hoisted_12 = { class: "text-gray-600 text-center" }

import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { 
	IonPage, 
	IonHeader, 
	IonToolbar, 
	IonTitle, 
	IonContent, 
	IonButtons, 
	IonBackButton,
	IonButton,
	IonSpinner,
	IonIcon
} from '@ionic/vue';
import { alertCircleOutline, chevronBackOutline, newspaperOutline } from 'ionicons/icons';
import { DateTime } from 'luxon';

import TopBar from '@/shared/components/TopBar.vue';
import NavigationBar from '@/shared/components/ui/NavigationBar.vue';
import { useSharedStore } from '@/stores/shared/sharedStore';
import useFavorite from '@/shared/composables/useFavorite';
import FeedItem from '@/shared/components/feed/FeedItem.vue';
import { DynamicPage, FeedItem as FeedItemType } from '@/shared/interfaces/dynamicPage';
import { useI18n } from 'vue-i18n';

// Route and router

export default /*@__PURE__*/_defineComponent({
  __name: 'FeedPage',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Get page ID from route params
const pageId = computed(() => {
	const id = route.params.id;
	return typeof id === 'string' ? parseInt(id, 10) : null;
});

// Use shared store for pages data
const sharedStore = useSharedStore();

// Use favorite composable
const { toggleFavorite } = useFavorite();

// Computed properties
const page = computed<DynamicPage | undefined>(() => {
	if (!sharedStore.pages || !pageId.value) return undefined;
	return sharedStore.pages.find(p => p.id === pageId.value && p.type === 'feed');
});

const isLoading = computed(() => !sharedStore.pages || sharedStore.pages.length === 0);
const error = computed(() => !page.value && sharedStore.pages.length > 0);

const pageTitle = computed(() => {
	if (isLoading.value) return t('pages.loading');
	if (error.value) return t('pages.error');
	if (page.value) return page.value.title;
	return t('pages.not_found');
});

const feedItems = computed<FeedItemType[]>(() => {
	if (!page.value || page.value.type !== 'feed') return [];

	// Handle both string and array content types
	if (typeof page.value.content === 'string') {
		try {
			return JSON.parse(page.value.content);
		} catch {
			return [];
		}
	}

	return Array.isArray(page.value.content) ? page.value.content : [];
});

const showNavigationBar = computed(() => {
	return page.value?.display_mode === 'content';
});

// Methods
const goBack = () => {
	router.push('/dashboard');
};

const formatDate = (dateString: string): string => {
	try {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	} catch {
		return dateString;
	}
};

const formatTime = (dateTimeString: string): string => {
	if (!dateTimeString) return '';
	
	try {
		return DateTime.fromFormat(dateTimeString, 'yyyy-MM-dd HH:mm:ss').toFormat('HH:mm');
	} catch {
		return '';
	}
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonContent), null, {
    default: _withCtx(() => [
      _createVNode(TopBar, { class: "px-[34px]" }, {
        left: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createVNode(_unref(IonButton), {
              class: "w-[30px] h-[30px] text-black ion-no-padding ion-no-margin hover:bg-white hover:rounded-full",
              shape: "round",
              onClick: goBack
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(IonIcon), {
                  icon: _unref(chevronBackOutline),
                  class: "ion-no-padding ion-no-margin text-black mr-[2px]"
                }, null, 8, ["icon"])
              ]),
              _: 1
            })
          ])
        ]),
        center: _withCtx(() => [
          _createElementVNode("div", _hoisted_2, _toDisplayString(pageTitle.value), 1)
        ]),
        right: _withCtx(() => [...(_cache[0] || (_cache[0] = []))]),
        _: 1
      }),
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_unref(IonSpinner), { name: "crescent" })
          ]))
        : (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("h2", _hoisted_6, _toDisplayString(_ctx.$t('pages.not_found')), 1),
                _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t('pages.not_found_description')), 1)
              ])
            ]))
          : (page.value && feedItems.value.length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(feedItems.value, (item, idx) => {
                  return (_openBlock(), _createBlock(FeedItem, {
                    "return-to": `/pages/feed/${pageId.value}`,
                    key: idx,
                    going: item.going || false,
                    id: item.id,
                    "canceled-at": item.canceled_at,
                    favorite: item.is_favorite || false,
                    summary: item.summary || '',
                    image: item.main_image_url || '',
                    link: `${item.type && item.type.includes('event') ? '/events' : '/news'}/${item.id}`,
                    type: item.type,
                    "location-address": item.address || '',
                    "happening-now": item.happening_now || false,
                    "happening-soon": item.happening_soon || false,
                    "is-event": item.type === 'event',
                    date: item.start_datetime || '',
                    "date-end": item.end_datetime || '',
                    "is-pinned": item.is_pinned || false,
                    hour: formatTime(item.start_datetime || ''),
                    "hour-end": formatTime(item.end_datetime || ''),
                    "show-logged-data": true,
                    "updated-at": item.updated_at || '',
                    title: item.title || item.name || '',
                    onOnFavoriteClicked: _unref(toggleFavorite)
                  }, null, 8, ["return-to", "going", "id", "canceled-at", "favorite", "summary", "image", "link", "type", "location-address", "happening-now", "happening-soon", "is-event", "date", "date-end", "is-pinned", "hour", "hour-end", "updated-at", "title", "onOnFavoriteClicked"]))
                }), 128))
              ]))
            : (page.value && feedItems.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("h2", _hoisted_11, _toDisplayString(_ctx.$t('pages.no_content_available')), 1),
                    _createElementVNode("p", _hoisted_12, _toDisplayString(_ctx.$t('pages.no_content_available_description')), 1)
                  ])
                ]))
              : _createCommentVNode("", true),
      (showNavigationBar.value)
        ? (_openBlock(), _createBlock(NavigationBar, {
            key: 4,
            "item-active": "pages"
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})